import React from "react";
import { Link } from "react-router-dom";
import { Heart, Plus } from "lucide-react";
import { money } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export function StarRating({ value = 0, size = 13 }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-sharoa-rose" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i}>{i <= Math.round(value) ? "★" : "☆"}</span>
      ))}
    </span>
  );
}

export default function ProductCard({ product }) {
  const { wishlist, toggleWishlist } = useAuth();
  const { addItem } = useCart();
  const inWish = wishlist?.includes(product.id);
  const discount = product.mrp && product.price < product.mrp
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  const quickAdd = (e) => {
    e.preventDefault();
    const v = (product.variants || []).find((x) => x.stock > 0);
    if (!v) { toast.error("Out of stock"); return; }
    addItem({ product_id: product.id, sku: v.sku, name: product.name, slug: product.slug,
      image: product.images?.[0], size: v.size, color: v.color, price: v.sale_price || v.price, quantity: 1 });
  };

  return (
    <div data-testid={`product-card-${product.slug}`} className="group bg-white border border-sharoa-line/60 hover:border-sharoa-rose/50 transition-colors relative">
      <Link to={`/product/${product.slug}`} className="block relative overflow-hidden sh-hover-zoom aspect-[3/4] bg-sharoa-ivory">
        <img src={product.images?.[0]} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.is_new && <span className="bg-sharoa-charcoal text-white text-[9px] uppercase tracking-widest px-2.5 py-1">New</span>}
          {discount > 0 && <span className="bg-sharoa-rose text-white text-[9px] uppercase tracking-widest px-2.5 py-1">{discount}% Off</span>}
          {product.non_returnable && <span className="bg-white/90 text-sharoa-charcoal text-[9px] uppercase tracking-widest px-2.5 py-1 border border-sharoa-line">Final Sale</span>}
        </div>
        <button data-testid={`wishlist-toggle-${product.slug}`}
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.id); }}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
          <Heart size={16} className={inWish ? "fill-sharoa-rose text-sharoa-rose" : "text-sharoa-charcoal"} />
        </button>
        <button data-testid={`quick-add-${product.slug}`} onClick={quickAdd}
          className="absolute bottom-0 inset-x-0 bg-sharoa-charcoal/95 text-white text-[11px] uppercase tracking-[0.16em] py-3 flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <Plus size={14} /> Quick Add
        </button>
      </Link>
      <div className="p-3.5">
        {product.return_eligible && !product.non_returnable && (
          <span className="text-[10px] text-sharoa-rose tracking-wide">7-Day Returns ♡</span>
        )}
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif text-[15px] text-sharoa-charcoal mt-1 leading-snug hover:text-sharoa-rose transition-colors">{product.name}</h3>
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="font-mono text-sm text-sharoa-charcoal">{money(product.price)}</span>
          {discount > 0 && <span className="font-mono text-xs text-sharoa-muted line-through">{money(product.mrp)}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-2.5">
          {(product.colors || []).slice(0, 5).map((c) => (
            <span key={c.name} title={c.name} className="w-3.5 h-3.5 rounded-full border border-sharoa-line" style={{ background: c.hex }} />
          ))}
        </div>
      </div>
    </div>
  );
}
